import { useEffect, useRef } from 'react';

/**
 * usePageLeaveDetect 훅의 옵션
 */
interface UsePageLeaveDetectOptions {
  /**
   * 사용자가 페이지를 떠날 때 실행할 콜백 함수
   */
  onLeave: () => void;

  /**
   * 페이지 이탈 감지 활성화 여부 (기본값: true)
   */
  enabled?: boolean;

  /**
   * 브라우저 탭이나 창을 닫을 때도 감지할지 여부 (기본값: true)
   */
  detectWindowClose?: boolean;

  /**
   * 뒤로가기/앞으로가기 등 히스토리 이동을 감지할지 여부 (기본값: true)
   */
  detectHistoryChange?: boolean;
}

/**
 * 사용자가 페이지를 떠날 때(창 닫기, 탭 닫기, 브라우저 종료, 뒤로가기 등)
 * 지정된 콜백 함수를 실행하는 훅
 *
 * @example
 * ```tsx
 * // 기본 사용법
 * usePageLeaveDetect({
 *   onLeave: () => console.log('사용자가 페이지를 떠납니다!')
 * });
 *
 * // 고급 사용법
 * usePageLeaveDetect({
 *   onLeave: savePageState,
 *   enabled: isDirty,
 *   detectWindowClose: true,
 *   detectHistoryChange: true
 * });
 * ```
 */
export const usePageLeaveDetect = ({
  onLeave,
  enabled = true,
  detectWindowClose = true,
  detectHistoryChange = true,
}: UsePageLeaveDetectOptions) => {
  // 콜백 함수를 ref로 유지하여 불필요한 리렌더링 방지
  const onLeaveRef = useRef(onLeave);

  // 콜백 ref 업데이트
  useEffect(() => {
    onLeaveRef.current = onLeave;
  }, [onLeave]);

  // visibilitychange 이벤트 핸들러
  useEffect(() => {
    if (!enabled) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        onLeaveRef.current();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [enabled]);

  // beforeunload 이벤트 핸들러(창/탭 닫기)
  useEffect(() => {
    if (!enabled || !detectWindowClose) return;

    const handleBeforeUnload = () => {
      onLeaveRef.current();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [enabled, detectWindowClose]);

  // popstate 이벤트 핸들러(뒤로가기/앞으로가기)
  useEffect(() => {
    if (!enabled || !detectHistoryChange) return;

    const handlePopState = () => {
      onLeaveRef.current();
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [enabled, detectHistoryChange]);

  // pagehide 이벤트 핸들러(페이지 언로드, 브라우저 캐시 진입)
  useEffect(() => {
    if (!enabled) return;

    const handlePageHide = () => {
      onLeaveRef.current();
    };

    window.addEventListener('pagehide', handlePageHide);

    return () => {
      window.removeEventListener('pagehide', handlePageHide);
    };
  }, [enabled]);
};
